<template>
    <section v-if="cards.length || activeCard" class="unstake-section" aria-label="AUBE STAKE">
        <article v-for="card in cards" :key="card.key" class="unstake-card">
            <div class="unstake-card__main">
                <div>
                    <p class="eyebrow">{{ $t('userWallet.unstake.eyebrow') }}</p>
                    <h2>{{ $t(cardTitleKey(card.symbol)) }}</h2>
                    <p>{{ $t(cardDescriptionKey(card.symbol)) }}</p>
                    <div class="unstake-card__amount">
                        <span class="unstake-amount-line">
                            <img :src="cardIcon(card.symbol)" alt="" width="20" height="20">
                            <span>{{ formatAmount(card.amount) }} {{ card.symbol }}</span>
                        </span>
                    </div>
                </div>
            </div>
            <div class="unstake-card__action">
                <button
                    class="primary-button"
                    type="button"
                    :disabled="card.processing"
                    @click="openModal(card)"
                >
                    {{ card.processing ? $t(cardProcessingKey(card.symbol)) : $t(cardButtonKey(card.symbol)) }}
                </button>
            </div>
        </article>

        <wallet-unstake-modal
            v-if="activeCard"
            :currency="activeCard.symbol"
            :amount="activeCard.amount"
            :withdrawal-ids="activeCard.withdrawal_ids"
            :last-updated-at="activeCard.last_updated_at"
            @close="activeCard = null"
            @claimed="onClaimed"
        />
    </section>
</template>

<script>
    import UserWalletService from '../../../services/UserWalletService';
    import walletFormatMixin from '../../../utils/walletFormatMixin';
    import WalletUnstakeModal from './WalletUnstakeModal.vue';

    // 現状 EDU-aube 側で Unstake/Claim の対象となる通貨。claimable API は
    // アクティブな非ネイティブ通貨を全件返すため、UI 文言（i18n）を用意している
    // この2種類のみカード表示する。
    const SUPPORTED_SYMBOLS = ['AUBE', 'OWL'];

    export default {
        name: 'WalletUnstakeSection',

        components: {
            WalletUnstakeModal,
        },

        mixins: [walletFormatMixin],

        data() {
            return {
                claimable: [],
                activeCard: null,
            };
        },

        computed: {
            cards() {
                const cards = [];

                this.claimable
                    .filter((row) => SUPPORTED_SYMBOLS.includes(row.symbol))
                    .forEach((row) => {
                        (Array.isArray(row.processing_claims) ? row.processing_claims : []).forEach((claim) => {
                            cards.push({
                                key: `${row.symbol}-processing-${claim.id}`,
                                symbol: row.symbol,
                                amount: claim.amount,
                                withdrawal_ids: [],
                                last_updated_at: claim.created_at,
                                processing: true,
                            });
                        });

                        // Each failed claim is its own box, never merged with new_claimable —
                        // same card look as the regular unstake box.
                        (Array.isArray(row.failed_claims) ? row.failed_claims : []).forEach((claim) => {
                            cards.push({
                                key: `${row.symbol}-failed-${claim.reward_claim_id}`,
                                symbol: row.symbol,
                                amount: claim.amount,
                                withdrawal_ids: claim.withdrawal_ids,
                                last_updated_at: claim.failed_at,
                                processing: false,
                            });
                        });

                        if (row.new_claimable && parseFloat(row.new_claimable.amount) > 0) {
                            cards.push({
                                key: `${row.symbol}-available`,
                                symbol: row.symbol,
                                amount: row.new_claimable.amount,
                                withdrawal_ids: row.new_claimable.withdrawal_ids,
                                last_updated_at: row.new_claimable.last_updated_at,
                                processing: false,
                            });
                        }
                    });

                return cards;
            },
        },

        created() {
            this.fetchClaimable();
        },

        methods: {
            cardIcon(symbol) {
                return symbol === 'OWL'
                    ? '/assets/images/wallets/owl.png'
                    : '/assets/images/wallets/aube.png';
            },

            cardTitleKey(symbol) {
                return `userWallet.unstake.${symbol.toLowerCase()}.cardTitle`;
            },

            cardDescriptionKey(symbol) {
                return `userWallet.unstake.${symbol.toLowerCase()}.cardDescription`;
            },

            cardButtonKey(symbol) {
                return `userWallet.unstake.${symbol.toLowerCase()}.button`;
            },

            cardProcessingKey(symbol) {
                return `userWallet.unstake.${symbol.toLowerCase()}.processing`;
            },

            async fetchClaimable(spinner = true) {
                try {
                    const response = await UserWalletService.getClaimable(spinner);
                    this.claimable = response.data.claimable;
                } catch (e) {}
            },

            /**
             * History tab 切替時: Claim が processing 中なら claimable を再取得し、
             * 完了後にボタン表示を更新する。
             */
            refreshIfProcessingClaim() {
                const hasProcessing = this.claimable.some((row) => (
                    Array.isArray(row.processing_claims) && row.processing_claims.length > 0
                ));
                if (!hasProcessing) {
                    return;
                }
                this.fetchClaimable(false);
            },

            openModal(card) {
                this.activeCard = card;
            },

            onClaimed() {
                // Modalは閉じない — 完了画面 (step === 'done' 「〜を受付けました」) をユーザーが
                // 「閉じる」を押すまで表示したままにする（@close="activeCard = null" で処理）。
                // Claim済みの withdrawal_ids は RESERVED に変わり claimable から即座に外れるため、
                // 再取得だけでカードの非表示/金額更新が反映される。
                this.fetchClaimable(false);
                this.$emit('claimed');
            },
        },
    };
</script>
